import Close from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { t } from 'i18next';
import { noop } from 'lodash-es';
import { useContext } from 'react';

import { BillingLine } from '~hooks/useBillingLines/models';
import {
  ConfirmDeleteLoadAndTicketsDialog,
  useDeleteLoadAndTickets,
} from '~hooks/useDeleteLoadAndTickets/useDeleteLoadAndTickets';
import { Job } from '~hooks/useJob/models';
import { Load } from '~hooks/useLoad/models';
import { Ticket } from '~hooks/useTickets/models';
import LoadsQueryContext from '~pages/Approvals/Files/components/JobsTable/ExpandedBlock/LoadsQueryContext';
import { useStores } from '~store';
import { isViewerAccountCustomer } from '~utils/user/user-utils';

import ChargesBillingLinesQueryContext from '../ChargesBillingLinesQueryContext';
import {
  getLineSettlementNumber,
  getSettlementBlock,
  SettlementBlock,
} from '../groupBillingLines';

const removeLoadBlockedKeys = {
  approved: 'approvals.approve_charges.settlement_blocked.approved',
  draft: 'approvals.approve_charges.settlement_blocked.draft',
  paid: 'approvals.approve_charges.remove_load_blocked.paid',
} satisfies Record<SettlementBlock, string>;

function getRemoveLoadBlockedReason(isLoadApproved: boolean, lines: BillingLine[]) {
  const settledLines = lines.filter(
    (line) => !line.isRejected && getLineSettlementNumber(line) !== null,
  );

  if (settledLines.length > 0) {
    const { block, settlements } = getSettlementBlock(settledLines);

    return t(removeLoadBlockedKeys[block], { settlements });
  }

  if (isLoadApproved) {
    return t('approvals.approve_charges.remove_load_blocked.load_approved');
  }

  return undefined;
}

interface ChargesRemoveLoadButtonProps {
  isLoadApproved: boolean;
  job: Job;
  lines: BillingLine[];
  load: Load;
  ticket?: Ticket;
}

export default function ChargesRemoveLoadButton({
  isLoadApproved,
  job,
  lines,
  load,
  ticket,
}: ChargesRemoveLoadButtonProps) {
  const { userStore } = useStores();
  const { reload } = useContext(ChargesBillingLinesQueryContext);
  const { refreshParentJob, removeStoredLoadById, removeStoredTicketsById } =
    useContext(LoadsQueryContext);
  const {
    confirmationDialogState,
    deleteLoadOrAlertIfItHasAssociatedTickets,
    isDeleting,
  } = useDeleteLoadAndTickets({
    onLoadDeleted: (loadId) => {
      removeStoredLoadById(loadId);
      refreshParentJob();
      reload();
    },
    onTicketsDeleted: removeStoredTicketsById,
  });

  const canRemoveLoad =
    userStore.getPermissions().canDeleteLoad &&
    !isViewerAccountCustomer(userStore, job.order?.account);

  if (!canRemoveLoad) {
    return null;
  }

  const blockedReason = getRemoveLoadBlockedReason(isLoadApproved, lines);
  const tooltip =
    blockedReason ??
    (ticket ? t('loads.remove_load_and_ticket') : t('loads.remove_load'));

  return (
    <>
      <Tooltip title={tooltip}>
        <Box component="span">
          <IconButton
            aria-label={tooltip}
            disabled={Boolean(blockedReason) || isDeleting}
            onClick={() =>
              void deleteLoadOrAlertIfItHasAssociatedTickets(
                ticket ? [ticket] : [],
                load.id,
              )
            }
            size="small"
            sx={(theme) => ({
              color: theme.brandV2.colors.treadGray3,
              '&:hover': { color: theme.brandV2.colors.treadBlack },
            })}
          >
            {isDeleting ? (
              <CircularProgress color="secondary" size={15} />
            ) : (
              <Close sx={{ fontSize: '16px' }} />
            )}
          </IconButton>
        </Box>
      </Tooltip>

      <ConfirmDeleteLoadAndTicketsDialog
        isOpen={confirmationDialogState.isOpen}
        onClose={confirmationDialogState.isOpen ? confirmationDialogState.onClose : noop}
        onConfirm={
          confirmationDialogState.isOpen
            ? confirmationDialogState.onConfirm
            : () => Promise.resolve()
        }
      />
    </>
  );
}
